/**
 * People (v2 frames "Hub · People" and "Hub · Person"). Search first, then a segmented filter,
 * then rows: roles as a phrase, status as a mark. Everything about a person is on the row's
 * press: one opaque sheet with their name, email, a segmented control per app for the role, the
 * password link and deactivate. "Invite someone" opens the same sheet, empty.
 *
 * Every change is pushed to the apps at once; a push that did not land shows as a mark on the
 * row ("Not updated in CX Academy") and the sheet offers Retry. The hub also retries every 5 min.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copyable, DateWell, ErrorNote, Field, Headline, Loading, Mark, Row, Search, Segmented, Sheet, useToast } from '@buckmason/cx-ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTitle } from '../lib/useTitle';
import { dateOnly } from '../lib/format';
import type { PersonRow, PushResult } from '../lib/types';
import { APPS, APP_KEYS, roleLabel, type AppKey, type HubRole } from '@shared/apps';

type Filter = 'all' | 'active' | 'invited' | 'deactivated';
const FILTERS: ReadonlyArray<{ value: Filter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'active', label: 'Active' },
  { value: 'invited', label: 'Invited' },
  { value: 'deactivated', label: 'Deactivated' },
];

const statusOf = (p: PersonRow): Exclude<Filter, 'all'> => (!p.active ? 'deactivated' : !p.hasPassword ? 'invited' : 'active');
// `apps`: the apps this hub offers (GET /people answers them); an app not launched yet is left out.
const failedApps = (p: PersonRow, apps: readonly AppKey[]) => apps.filter((k) => p.sync[k]?.error);

/** "Member · Agent · No access": always the hub role, then each app, in the header's order. */
function rolesPhrase(p: Pick<PersonRow, 'hubRole' | 'access'>, live: readonly AppKey[]) {
  const apps = live.map((k) => (p.access[k] ? roleLabel(k, p.access[k]!) : 'No access'));
  return [p.hubRole === 'admin' ? 'Admin' : 'Member', ...apps].join(' · ');
}

function StatusMark({ p, apps }: { p: PersonRow; apps: readonly AppKey[] }) {
  const failed = failedApps(p, apps);
  if (failed.length) return <Mark kind="missed">Not updated in {failed.map((k) => APPS[k].name).join(', ')}</Mark>;
  const s = statusOf(p);
  if (s === 'deactivated') return <Mark kind="na">Deactivated</Mark>;
  if (s === 'invited') return <Mark kind="open">Invited {dateOnly(p.createdAt)}</Mark>;
  return <Mark kind="done">Active</Mark>;
}

export function People() {
  useTitle('People');
  const people = useQuery({ queryKey: ['people'], queryFn: () => api.get<{ items: PersonRow[]; apps?: AppKey[] }>('/api/id/people') });
  const live = people.data?.apps ?? APP_KEYS;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<PersonRow | 'new' | null>(null);

  const rows = people.data?.items ?? [];
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((p) => (filter === 'all' || statusOf(p) === filter) && (!q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)));
  }, [rows, query, filter]);

  if (people.isLoading) return <Loading what="people" rows={6} />;
  if (people.error) return <ErrorNote error={people.error} />;

  const count = rows.length === 1 ? 'One person' : `${rows.length} people`;

  return (
    <div className="page arrive">
      <Headline
        title="People"
        action={
          <button type="button" className="primary" onClick={() => setOpen('new')}>
            Invite someone
          </button>
        }
      >
        {count}. Roles are set here once; Quality Review and Academy follow on their next request.
      </Headline>

      <div className="people-filters">
        <Search value={query} onChange={setQuery} placeholder="Search people" />
        <Segmented label="Show" value={filter} options={FILTERS} onChange={setFilter} />
      </div>

      <div className="v2-group is-wide people-list">
        <div className="people-head" aria-hidden="true">
          <span>Person</span>
          <span>Roles · Hub · {live.map((k) => APPS[k].name).join(' · ')}</span>
          <span />
        </div>
        {shown.map((p) => (
          <Row key={p.id} size={56} onClick={() => setOpen(p)} title={p.name} meta={p.email} className="people-row">
            <span className="people-roles">{rolesPhrase(p, live)}</span>
            <span className="people-status">
              <StatusMark p={p} apps={live} />
            </span>
          </Row>
        ))}
        {shown.length === 0 && <p className="empty">Nobody matches.</p>}
      </div>

      <Sheet open={open !== null} onClose={() => setOpen(null)} label={open === 'new' ? 'Invite someone' : open ? open.name : 'Person'} wide>
        {open !== null && <PersonSheet key={open === 'new' ? 'new' : open.id} person={open === 'new' ? null : open} apps={live} onDone={() => setOpen(null)} />}
      </Sheet>
    </div>
  );
}

type Draft = { name: string; email: string; startDate: string; hubRole: HubRole; access: Partial<Record<AppKey, string | null>> };

/** One person, one sheet: everything you can change about them, and Save. */
function PersonSheet({ person, apps, onDone }: { person: PersonRow | null; apps: readonly AppKey[]; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { me, reload } = useAuth();
  const isNew = person === null;
  const self = person?.id === me?.id;
  const [draft, setDraft] = useState<Draft>(() => ({
    name: person?.name ?? '',
    email: person?.email ?? '',
    // The stored date as it is (yyyy-mm-dd). Through new Date() it is read as UTC midnight and a
    // local getter moves it a day back west of UTC, so every save shifted First day.
    startDate: person?.startDate ? person.startDate.slice(0, 10) : '',
    hubRole: person?.hubRole ?? 'member',
    access: person ? { ...person.access } : { qa: 'agent', academy: 'agent' },
  }));
  const [link, setLink] = useState<{ url: string; reset: boolean } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [sync, setSync] = useState<PushResult[]>([]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['people'] });
    if (self) void reload(); // your own access changes what Home and the switcher show
  };

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email.trim());
  const nameError = !draft.name.trim() ? 'Enter their name.' : undefined;
  const emailError = isNew && !emailOk ? 'Enter a valid email address.' : undefined;
  const [tried, setTried] = useState(false);

  const save = useMutation({
    mutationFn: async (): Promise<{ person: PersonRow; sync: PushResult[]; inviteUrl?: string }> => {
      const access = Object.fromEntries(APP_KEYS.map((k) => [k, draft.access[k] || null]));
      if (isNew) {
        return api.post<{ person: PersonRow; inviteUrl: string; sync: PushResult[] }>('/api/id/people', {
          name: draft.name.trim(),
          email: draft.email.trim(),
          hubRole: draft.hubRole,
          startDate: draft.startDate || null,
          access,
        });
      }
      return api.patch<{ person: PersonRow; sync: PushResult[] }>(`/api/id/people/${person.id}`, {
        name: draft.name.trim(),
        hubRole: draft.hubRole,
        startDate: draft.startDate || null,
        access,
      });
    },
    onSuccess: (d) => {
      refresh();
      const failed = d.sync.filter((s) => !s.ok);
      if (d.inviteUrl) {
        setLink({ url: d.inviteUrl, reset: false });
        setSync(failed);
        return;
      }
      if (failed.length) {
        setSync(failed);
        return;
      }
      toast(`Saved ${d.person.name}`);
      onDone();
    },
  });

  const active = useMutation({
    mutationFn: (value: boolean) => api.patch<{ person: PersonRow }>(`/api/id/people/${person!.id}`, { active: value }),
    onSuccess: (d) => {
      refresh();
      toast(d.person.active ? `${d.person.name} can sign in again` : `${d.person.name} is deactivated`);
      onDone();
    },
  });

  const newLink = useMutation({
    mutationFn: () => api.post<{ url: string; reset: boolean }>(`/api/id/people/${person!.id}/password-link`),
    onSuccess: (d) => setLink(d),
  });

  const retry = useMutation({
    mutationFn: () => api.post(`/api/id/people/${person!.id}/sync`),
    onSuccess: () => {
      refresh();
      toast('Sent again');
      onDone();
    },
  });

  const setRole = (k: AppKey, v: string) => setDraft({ ...draft, access: { ...draft.access, [k]: v === 'none' ? null : v } });
  const failed = person ? failedApps(person, apps) : [];

  if (link) {
    return (
      <>
        <h2>{link.reset ? 'A new link' : `${draft.name} is invited`}</h2>
        <p className="muted" style={{ margin: 0 }}>
          Send this to {draft.name}. It works once, for 72 hours{link.reset && person?.hasPassword ? ', and signs them out everywhere' : ''}.
        </p>
        <Copyable value={link.url} label="Password link" />
        {sync.length > 0 && <Mark kind="missed">Not updated in {sync.map((s) => APPS[s.app].name).join(', ')} yet; the hub retries every 5 minutes</Mark>}
        <div className="sheet-actions">
          <button type="button" className="primary" onClick={onDone}>
            Done
          </button>
        </div>
      </>
    );
  }

  return (
    <form
      className="person-sheet"
      onSubmit={(e) => {
        e.preventDefault();
        setTried(true);
        if (!nameError && !emailError) save.mutate();
      }}
    >
      <div className="person-sheet-head">
        <div>
          <h2>{isNew ? 'Invite someone' : person.name}</h2>
          {person && <StatusMark p={person} apps={apps} />}
        </div>
        {person?.active && (
          <button type="button" className="quiet" disabled={newLink.isPending} onClick={() => newLink.mutate()}>
            {person.hasPassword ? 'Reset password' : 'Send a new link'}
          </button>
        )}
      </div>

      <div className="person-sheet-fields">
        <Field label="Name" error={tried ? nameError : undefined}>
          <input type="text" data-autofocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        </Field>
        <Field label="Email" error={tried ? emailError : undefined}>
          <input
            type="email"
            value={draft.email}
            readOnly={!isNew}
            placeholder="name@buckmason.com"
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </Field>
      </div>

      <div className="person-sheet-roles">
        <span className="field-label">Roles</span>
        <div className="person-role">
          <span>Hub</span>
          <Segmented
            label="Hub role"
            value={draft.hubRole}
            disabled={self}
            options={[
              { value: 'member', label: 'Member' },
              { value: 'admin', label: 'Admin' },
            ]}
            onChange={(v) => setDraft({ ...draft, hubRole: v })}
          />
        </div>
        {/* Only the apps offered; the save still sends every app's access, so a hidden one is kept. */}
        {apps.map((k) => (
          <div key={k} className="person-role">
            <span>{APPS[k].name}</span>
            <Segmented
              label={`${APPS[k].name} role`}
              value={draft.access[k] ?? 'none'}
              options={[{ value: 'none', label: 'None' }, ...[...APPS[k].roles].reverse().map((r) => ({ value: r.value, label: r.label }))]}
              onChange={(v) => setRole(k, v)}
            />
          </div>
        ))}
      </div>

      <Field label="First day" hint="Academy counts Day 1 from here">
        <DateWell value={draft.startDate} placeholder="3 Nov 2026" onChange={(v) => setDraft({ ...draft, startDate: v })} />
      </Field>

      {(save.error || active.error || newLink.error || retry.error) && <ErrorNote error={save.error ?? active.error ?? newLink.error ?? retry.error} />}
      {sync.length > 0 && <Mark kind="missed">Saved, but not updated in {sync.map((s) => APPS[s.app].name).join(', ')} yet</Mark>}

      {confirming ? (
        <div className="person-confirm" role="alertdialog" aria-label={`Deactivate ${person!.name}?`}>
          <p>
            Deactivate {person!.name}? They are signed out of every app and cannot sign in. Their reviews and training history stay.
          </p>
          <div className="sheet-actions">
            <button type="button" className="quiet" onClick={() => setConfirming(false)}>
              Cancel
            </button>
            <button type="button" className="danger" disabled={active.isPending} onClick={() => active.mutate(false)}>
              {active.isPending ? 'Deactivating…' : 'Deactivate'}
            </button>
          </div>
        </div>
      ) : (
        <div className="sheet-actions">
          {person && person.active && !self && (
            <button type="button" className="danger" onClick={() => setConfirming(true)}>
              Deactivate
            </button>
          )}
          {person && !person.active && (
            <button type="button" className="quiet" disabled={active.isPending} onClick={() => active.mutate(true)}>
              Activate
            </button>
          )}
          {failed.length > 0 && (
            <button type="button" className="quiet" disabled={retry.isPending} onClick={() => retry.mutate()}>
              Retry
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="quiet" onClick={onDone}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Invite' : 'Save'}
          </button>
        </div>
      )}
    </form>
  );
}
